import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { TransactionsListSkeleton } from './TransactionsListSkeleton';
import { TransactionsListView } from './TransactionsListView';
import {
    useTransactionsList,
    type TransactionsListLogic,
} from './useTransactionsList';

type TransactionsListProps = {
    date: string;
};

/**
 * Self-served transactions list section: owns its data hook (server-paged), its
 * loading skeleton, and its own `ErrorBoundary` — so a fetch or render failure
 * shows an inline error here without taking down the rest of the page. The page
 * passes only the selected `date`.
 */
export const TransactionsList: React.FC<TransactionsListProps> = ({ date }) => {
    const list = useTransactionsList(date);
    const { t } = useTranslation('pages');

    return (
        <ErrorBoundary
            data-testid="transactions-list-error-boundary"
            fallbackMessage={t('transactions.errorMessage')}
            id="transactions-list-error-boundary"
            onRetry={list.onRetry}
            retryLabel={t('transactions.errorRetry')}
            title={t('transactions.errorTitle')}
        >
            <TransactionsListContent {...list} />
        </ErrorBoundary>
    );
};

/**
 * Boundary child: surfaces an initial load error by throwing (so the parent
 * `ErrorBoundary` catches it), shows the skeleton on the first load, otherwise
 * renders the grid. The throw must live in a child of the boundary, not the
 * component that mounts it.
 */
const TransactionsListContent: React.FC<TransactionsListLogic> = ({
    items,
    totalItems,
    totalPages,
    page,
    rowsPerPage,
    pageWindow,
    isLoading,
    error,
    onPageChange,
    onRowsPerPageChange,
}) => {
    if (error && items.length === 0) {
        throw error;
    }

    if (isLoading && items.length === 0) {
        return <TransactionsListSkeleton />;
    }

    return (
        <TransactionsListView
            items={items}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={page}
            pageWindow={pageWindow}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            totalPages={totalPages}
        />
    );
};
